export const SERVICES = [
  {
    slug: 'talent-management',
    eyebrow: 'Division One',
    img: 'https://media.base44.com/images/public/6a68984e1435c2ee512e6e95/e4707347c_generated_image.png',
    deliverables: [
      'Exclusive representation agreement with dedicated account lead',
      'Quarterly growth plan with documented format and cadence strategy',
      'Rate card benchmarking and full commercial negotiation',
      'Campaign deliverables produced, approved and scheduled end-to-end',
      'Verified post-campaign reporting against pre-agreed benchmarks',
      'Reputation monitoring and escalation protocol',
    ],
    idealFor: [
      'Brands seeking long-term ambassadorships rather than one-off posts',
      'Category launches requiring genuine audience overlap',
      'Multi-market campaigns needing local creative fluency',
    ],
    title: 'Talent Management & Influence',
    tagline: 'Representation, growth architecture and monetisation for a curated global roster.',
    summary:
      'We manage an elite, highly curated international portfolio of 10 lifestyle, fashion, tech, and cultural creators. From platform growth architecture to premium monetization models, we scale modern influence.',
    intro:
      'Ahana Digital operates as a full-service management division: we own the commercial, creative and operational load so our creators can concentrate entirely on output. Every roster member is represented exclusively, with dedicated account leadership and a documented growth plan reviewed each quarter.',
    capabilities: [
      {
        title: 'Platform Growth Architecture',
        body:
          'Format research, publishing cadence, hook engineering and retention analysis across every surface a creator operates on. Growth is treated as a system, not a streak.',
      },
      {
        title: 'Premium Monetisation Models',
        body:
          'Layered revenue design — brand partnerships, licensing, owned product lines and long-term ambassadorships — structured to protect creator equity and audience trust.',
      },
      {
        title: 'Brand Alignment & Vetting',
        body:
          'We decline more partnerships than we accept. Every placement is assessed for category fit, audience tolerance and long-term reputational effect before terms are discussed.',
      },
      {
        title: 'Contract & Commercial Negotiation',
        body:
          'Rate benchmarking, usage rights, exclusivity windows and deliverable scoping handled by our commercial desk with legal oversight from our London office.',
      },
      {
        title: 'Content Operations',
        body:
          'Production scheduling, editing pipelines, asset management and approvals — so campaign deliverables land on time, in specification, every cycle.',
      },
      {
        title: 'Reputation & Crisis Management',
        body:
          'Proactive monitoring, response protocols and escalation paths that protect both creator and partner brand equity.',
      },
    ],
    process: [
      { step: 'Discovery', body: 'Objectives, category, market and audience requirements defined with the client.' },
      { step: 'Roster Matching', body: 'We shortlist creators by genuine audience overlap, not follower count.' },
      { step: 'Commercial Structuring', body: 'Deliverables, usage rights and rates agreed and contracted.' },
      { step: 'Execution', body: 'Content produced, approved and published against an agreed calendar.' },
      { step: 'Reporting', body: 'Performance measured against pre-set benchmarks with full data transparency.' },
    ],
    pricing: {
      note:
        'All figures are entry points for scoping purposes and are quoted in USD, exclusive of production costs, media spend and applicable taxes. Rates are benchmarked per creator against reach, category demand and requested exclusivity, and confirmed only under signed contract.',
      tiers: [
        {
          name: 'Single Campaign',
          structure: 'Per Placement',
          summary:
            'One creator, one defined campaign window. Suited to launches and seasonal activations requiring precision rather than volume.',
          from: 'From $15,000',
          basis: 'Per creator, per campaign',
          includes: [
            'Roster matching on genuine audience overlap',
            'Contracted deliverables and usage rights',
            'Creative direction and approval cycles',
            'Verified post-campaign performance report',
          ],
        },
        {
          name: 'Retained Ambassadorship',
          structure: 'Monthly Retainer',
          featured: true,
          summary:
            'A continuous relationship between brand and creator, managed as a long-term equity position rather than a transaction.',
          from: 'From $38,000',
          basis: 'Per month, six-month minimum',
          includes: [
            'Dedicated account leadership',
            'Quarterly growth and content strategy',
            'Priority creator availability and exclusivity windows',
            'Monthly reporting against agreed benchmarks',
            'Reputation monitoring and escalation protocol',
          ],
        },
        {
          name: 'Portfolio Programme',
          structure: 'Bespoke',
          summary:
            'Multi-creator, multi-market representation programmes for enterprises operating across several categories at once.',
          from: 'On Application',
          basis: 'Scoped per market and category',
          includes: [
            'Multi-creator roster architecture',
            'Cross-market localisation strategy',
            'Category exclusivity across the roster',
            'Consolidated executive reporting',
          ],
        },
      ],
    },
    stats: [
      { k: '10', v: 'Creators Represented' },
      { k: '9', v: 'Core Markets' },
      { k: '48M+', v: 'Combined Reach' },
      { k: '8.7%', v: 'Avg. Engagement' },
    ],
  },
  {
    slug: 'digital-production',
    eyebrow: 'Division Two',
    img: 'https://media.base44.com/images/public/6a68984e1435c2ee512e6e95/fe4bdf37e_generated_image.png',
    deliverables: [
      'Creative territories presented with rationale and production estimates',
      'Production-ready interactive builds, tested across devices',
      'Motion, 3D and campaign assets to broadcast and paid-media spec',
      'Complete identity systems with application guidelines',
      'Lifecycle messaging and CRM sequencing configured and documented',
      'Full licence transfer and versioned asset handover',
    ],
    idealFor: [
      'Enterprises consolidating fragmented brand assets into one system',
      'Product launches requiring interactive or immersive media',
      'Global rollouts needing localised, channel-specific versioning',
    ],
    title: 'Digital Creation & Production Studio',
    tagline: 'Enterprise-grade digital assets, brand systems and communication architecture.',
    summary:
      'Beyond talent management, Ahana Digital functions as an enterprise-grade digital studio. We build high-fidelity interactive media, immersive assets, corporate branding suites, and automated communication architecture for global brands.',
    intro:
      'Our production division exists because modern campaigns require more than a post. We build the assets, systems and infrastructure that surround influence — from interactive brand experiences to the automated communication layers that convert attention into pipeline.',
    capabilities: [
      {
        title: 'High-Fidelity Interactive Media',
        body:
          'Web experiences, product configurators and interactive campaign microsites engineered for performance and accessibility on every device.',
      },
      {
        title: 'Immersive & Motion Assets',
        body:
          '3D product visualisation, motion graphics and animated brand systems produced to broadcast and paid-media specification.',
      },
      {
        title: 'Corporate Branding Suites',
        body:
          'Complete identity systems: logotype, typographic hierarchy, colour architecture, application guidelines and full asset libraries.',
      },
      {
        title: 'Automated Communication Architecture',
        body:
          'Lifecycle messaging, CRM sequencing and intelligent routing that keeps enterprise audiences engaged without manual overhead.',
      },
      {
        title: 'Commercial Campaign Production',
        body:
          'Concept, direction, shoot and post — delivered as fully licensed campaign packages ready for multi-market deployment.',
      },
      {
        title: 'Localisation & Delivery',
        body:
          'Multi-language adaptation, format versioning and channel-specific delivery across every surface a campaign occupies.',
      },
    ],
    process: [
      { step: 'Brief Intake', body: 'Commercial objective, constraints and success criteria documented.' },
      { step: 'Concept & Direction', body: 'Creative territories presented with rationale and production estimates.' },
      { step: 'Production', body: 'Build, shoot or render executed by the assigned specialist team.' },
      { step: 'Review Cycles', body: 'Structured approval rounds with versioned assets and clear sign-off.' },
      { step: 'Delivery & Handover', body: 'Final assets, licences and documentation transferred in full.' },
    ],
    pricing: {
      note:
        'All figures are entry points for scoping purposes and are quoted in USD, exclusive of third-party licensing, media spend and applicable taxes. Every engagement is confirmed against a written brief with fixed deliverables, review rounds and handover terms.',
      tiers: [
        {
          name: 'Defined Project',
          structure: 'Fixed Scope',
          summary:
            'A single asset set or build, quoted against a written brief with fixed review rounds and a firm delivery date.',
          from: 'From $23,000',
          basis: 'Per project',
          includes: [
            'Creative territories with written rationale',
            'Production by the assigned specialist team',
            'Two structured review cycles',
            'Full licence transfer on handover',
          ],
        },
        {
          name: 'Studio Retainer',
          structure: 'Monthly Retainer',
          featured: true,
          summary:
            'Reserved studio capacity for brands with continuous production demand across channels and markets.',
          from: 'From $30,000',
          basis: 'Per month, three-month minimum',
          includes: [
            'Guaranteed monthly production capacity',
            'Standing creative and art direction',
            'Channel-specific format versioning',
            'Automated communication architecture upkeep',
            'Quarterly performance and asset audit',
          ],
        },
        {
          name: 'Enterprise Build',
          structure: 'Bespoke',
          summary:
            'Full identity systems, immersive platforms and communication infrastructure delivered as a governed programme.',
          from: 'On Application',
          basis: 'Scoped per programme',
          includes: [
            'Complete identity and asset library',
            'Interactive or immersive platform build',
            'Multi-language localisation across 14 markets',
            'Documentation, governance and team training',
          ],
        },
      ],
    },
    stats: [
      { k: '14', v: 'Delivery Languages' },
      { k: '4–8', v: 'Week Turnaround' },
      { k: '20+', v: 'Channels Supported' },
      { k: '100%', v: 'Owned Licensing' },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);